<script setup lang="ts">
import { computed } from 'vue'
import { LibraryBig } from '@lucide/vue'
import { Progress } from 'catalyst-vue'
import { useLibraryStore } from '../../stores/library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { useComicCover } from '../../lib/comic-cover'
import { collectionBadge, collectionProgress, collectionStatus, type CollectionCell } from '../../lib/collection-presentation'
import CollectionCardMenu, { type CollectionAction } from './collection-card-menu.vue'
import ComicSelectBox from './comic-select-box.vue'
import ComicStatusDot from './comic-status-dot.vue'

/**
 * 書架上的集合格：封面取自封面來源成員、標題、「已讀完 N／M 本」進度條。
 * 多選模式下代表它的全部成員；非多選時點擊開啟集合頁。
 */
const props = defineProps<{ item: CollectionCell }>()
const emit = defineEmits<{ open: [cell: CollectionCell]; read: [cell: CollectionCell]; edit: [cell: CollectionCell]; dissolve: [cell: CollectionCell]; delete: [cell: CollectionCell] }>()
const store = useLibraryStore()
const entries = useLibraryEntriesStore()
const collection = computed(() => props.item.collection)
// 封面元件吃作品：有本機那一份就走本機縮圖，否則走雲端封面。
const coverScope = computed(() => props.item.cover.libraryId === 'cloud' ? 'cloud' as const : 'local' as const)
const { element, cover, failed } = useComicCover(() => props.item.cover, () => coverScope.value)
const selecting = computed(() => entries.selectionMode)
const selected = computed(() => entries.selection.has(props.item.id))
const status = computed(() => collectionStatus(props.item.members, store))
const progress = computed(() => collectionProgress(props.item))
const merged = computed(() => props.item.mode === 'merged')
/** 非多選時點格子：集合模式開集合頁，合併模式直接開閱讀器。 */
function activate() { if (merged.value) emit('read', props.item); else emit('open', props.item) }
function selectAction(key: CollectionAction) {
  if (key === 'read') { emit('read', props.item); return }
  if (key === 'open') { emit('open', props.item); return }
  if (key === 'edit') { emit('edit', props.item); return }
  if (key === 'dissolve') { emit('dissolve', props.item); return }
  emit('delete', props.item)
}
</script>

<template>
  <article ref="element" class="comic-card collection-card">
    <div class="cover-stage" data-row-action-reveal-scope>
      <button v-if="selecting" type="button" class="comic-read-link" :aria-label="`選取集合 ${collection.title}`" @click="event => entries.toggleSelected(item.id, event.shiftKey)" />
      <button v-else type="button" class="comic-read-link" :aria-label="merged ? `閱讀合併冊 ${collection.title}` : `開啟集合 ${collection.title}`" @click="activate" />
      <ComicSelectBox
        v-if="selecting"
        class="comic-card-select"
        :selected="selected"
        :title="`集合 ${collection.title}`"
        @toggle="extend => entries.toggleSelected(item.id, extend)"
      />
      <img v-if="cover && !failed" :src="cover" :alt="`${collection.title}，封面`" class="comic-cover" @error="failed = true" />
      <div v-else class="cover-fallback"><LibraryBig :size="28" :stroke-width="1" aria-hidden="true" /><span>{{ collection.memberCount }} 本</span></div>
      <!-- 集合的記號：右下角的本數，讓它在書架上一眼就跟單本作品分得開。 -->
      <span class="collection-badge" aria-hidden="true">{{ collectionBadge(item) }}</span>
      <ComicStatusDot v-if="status" class="cover-status" :status="status" />
      <CollectionCardMenu class="comic-card-menu" :title="collection.title" :merged="merged" :data-collection-id="collection.id" @select="selectAction" />
    </div>
    <div class="comic-caption">
      <h3 :title="collection.title">{{ collection.title }}</h3>
      <Progress v-if="progress" class="comic-progress" size="sm" color="primary" :value="progress.percent" :aria-label="progress.label" />
    </div>
  </article>
</template>
